import React, { useMemo, useState, useEffect } from 'react';
import { X, QrCode, ShoppingBag, Check, Loader2, ExternalLink, Mail, Zap, Star, Flame, Truck, ShieldCheck, MapPin, CalendarClock, Tag } from 'lucide-react';
import { apparelProducts, autobookPayUrl, invoiceEmail, iceLogo } from '../mock';
import { createOrder, listProductPrices, validatePromo, getLimitedStock, startCheckout } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import PaymentSection from '../components/PaymentSection';

const CATEGORIES = ['All', 'Apparel', 'Headwear', 'Fan Gear'];

// Local Pickup — practice field
const PICKUP_LOCATION = 'McLaughlin Playground (across from 239 Parker Hill Ave, Roxbury, MA)';
const PICKUP_LEAD_DAYS = 3;
const PICKUP_MAP_URL = 'https://maps.google.com/?q=29+Parker+Hill+Ave+Roxbury+MA';

function computePickupDate() {
  const d = new Date();
  d.setDate(d.getDate() + PICKUP_LEAD_DAYS);
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

// Product-level enhancements — pure UI badges (no data model changes)
const HOT_IDS = new Set(['hoodie-ice', 'tshirt-classic', 'visor-red-hair']);
const NEW_IDS = new Set(['cap-camo-fitted', 'visor-red-hair', 'tshirt-long-sleeve']);
const LIMITED_IDS = new Set(['hoodie-red']);
const SPECIAL_EDITION_HOODIE_ID = 'hoodie-red';
const SOLD_OUT_SIZES = { 'tshirt-classic': new Set(['L', 'XL']), 'hoodie-red': new Set(['L', 'XL']) };
const SIZE_LABELS = { S: 'Small', M: 'Medium', L: 'Large', XL: 'Extra Large', XXL: '2X Large', YS: 'Youth Small', YM: 'Youth Medium', YL: 'Youth Large' };

const isSizeAvailable = (productId, size) => !SOLD_OUT_SIZES[productId]?.has(size);
const soldOutSummary = (productId) => (SOLD_OUT_SIZES[productId] ? `${[...SOLD_OUT_SIZES[productId]].map(sizeLabel).join(' & ')} Sold Out · All other sizes available` : '');
const sizeLabel = (size) => SIZE_LABELS[size] || size;

const MARQUEE_ITEMS = [
  'PATRIOTS TEAM SHOP',
  'PICKUP AT PRACTICE',
  'SCAN → PAY IN SECONDS',
  'GAME DAY READY',
  'CHEER & FOOTBALL GEAR',
  'BROOKLINE • JAMAICA PLAIN',
  'REP THE CREST'
];

const emptyForm = {
  productId: '',
  productName: '',
  variant: '',
  size: '',
  quantity: 1,
  unitPrice: 0,
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  notes: '',
  promoCode: ''
};

export default function Apparel() {
  const { toast } = useToast();
  const [category, setCategory] = useState('All');
  const [activeProduct, setActiveProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { invoiceNumber, total, productName, pickupLocation, pickupReadyDate }
  const [priceOverrides, setPriceOverrides] = useState({});
  const [promoResult, setPromoResult] = useState(null); // { valid, discount, subtotal, total, message, code }
  const [iceStock, setIceStock] = useState(null); // { limit, sold, remaining, soldOut }
  const pickupReady = useMemo(computePickupDate, [activeProduct]);

  // Load price overrides from backend
  useEffect(() => {
    listProductPrices().then(setPriceOverrides).catch(() => {});
    // Load ICE limited-edition stock counter
    getLimitedStock('hoodie-red', 20).then(setIceStock).catch(() => {});
  }, []);

  // Merge product catalog with backend price overrides
  const productsWithPrices = useMemo(
    () => apparelProducts.map((p) => ({
      ...p,
      price: priceOverrides[p.id] != null ? priceOverrides[p.id] : p.price
    })),
    [priceOverrides]
  );

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(autobookPayUrl)}&size=300x300&bgcolor=ffffff&color=0a1f44&margin=8`;

  const filtered = useMemo(() => {
    if (category === 'All') return productsWithPrices;
    return productsWithPrices.filter((p) => p.category === category);
  }, [category, productsWithPrices]);

  const featured = productsWithPrices.find((p) => p.id === 'hoodie-ice') || productsWithPrices[0];

  const openBuy = (product) => {
    setActiveProduct(product);
    setForm({
      ...emptyForm,
      productId: product.id,
      productName: product.name,
      variant: product.variants[0] || '',
      size: product.sizes.find((size) => isSizeAvailable(product.id, size)) || '',
      unitPrice: product.price ?? 0
    });
    setPromoResult(null);
    setConfirmation(null);
  };

  // Live promo preview whenever qty / code / product changes
  useEffect(() => {
    if (!activeProduct) return;
    const code = (form.promoCode || '').trim();
    if (!code) { setPromoResult(null); return; }
    let ignore = false;
    (async () => {
      try {
        const r = await validatePromo({
          productId: form.productId,
          quantity: parseInt(form.quantity, 10) || 1,
          unitPrice: parseFloat(form.unitPrice) || 0,
          promoCode: code,
        });
        if (!ignore) setPromoResult(r);
      } catch (_e) {
        if (!ignore) setPromoResult(null);
      }
    })();
    return () => { ignore = true; };
  }, [activeProduct, form.promoCode, form.quantity, form.productId, form.unitPrice]);

  const closeModal = () => {
    setActiveProduct(null);
    setConfirmation(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isSizeAvailable(form.productId, form.size)) {
      toast({ title: 'That size is sold out', description: 'Medium and Youth Large remain available.' });
      return;
    }
    if (!form.customerName || !form.customerEmail) {
      toast({ title: 'Please fill in your name and email' });
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        productId: form.productId,
        productName: form.productName,
        variant: form.variant,
        size: form.size,
        quantity: parseInt(form.quantity, 10) || 1,
        unitPrice: parseFloat(form.unitPrice) || 0,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone,
        notes: form.notes,
        promoCode: form.promoCode
      });
      setConfirmation({
        orderId: order.id,
        invoiceNumber: order.invoiceNumber,
        subtotal: order.subtotal ?? order.total,
        discount: order.discount ?? 0,
        promoCode: order.promoCode || '',
        total: order.total,
        productName: order.productName,
        pickupLocation: order.pickupLocation || PICKUP_LOCATION,
        pickupReadyDate: order.pickupReadyDate
      });
    } catch (err) {
      toast({ title: 'Order failed', description: err.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-body bg-[#0a1f44]">
      {/* ANIMATED HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-gradient-to-br from-[#0a1f44] via-[#0d2555] to-[#1a1a2e]">
        {/* Star field overlay */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute top-8 left-16 w-1 h-1 bg-white animate-pulse" />
          <div className="absolute top-24 left-1/3 w-1.5 h-1.5 bg-white animate-pulse" style={{ animationDelay: '.3s' }} />
          <div className="absolute top-48 left-2/3 w-1 h-1 bg-white animate-pulse" style={{ animationDelay: '.7s' }} />
          <div className="absolute top-16 right-1/4 w-2 h-2 bg-[#c8102e] animate-pulse" style={{ animationDelay: '1.1s' }} />
          <div className="absolute top-64 left-1/4 w-1 h-1 bg-white animate-pulse" style={{ animationDelay: '1.5s' }} />
          <div className="absolute top-32 right-16 w-1.5 h-1.5 bg-white animate-pulse" style={{ animationDelay: '.5s' }} />
          <div className="absolute bottom-20 left-1/2 w-1 h-1 bg-[#c8102e] animate-pulse" style={{ animationDelay: '.9s' }} />
        </div>

        {/* Colored blobs */}
        <div className="hidden sm:block absolute -top-32 -left-32 w-96 h-96 bg-[#c8102e]/20 rounded-full blur-3xl" />
        <div className="hidden sm:block absolute -bottom-32 -right-32 w-96 h-96 bg-[#c8102e]/10 rounded-full blur-3xl" />
        <div className="hidden md:block absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5 mb-6 shadow-lg shadow-[#c8102e]/50">
              <Flame className="w-3 h-3" /> Official Team Store · Now Open
            </div>
            <h1 className="font-display text-white leading-[0.85] tracking-tight" data-testid="apparel-hero-heading">
              <span className="block text-5xl sm:text-6xl md:text-8xl lg:text-9xl">REP THE</span>
              <span className="block text-5xl sm:text-6xl md:text-8xl lg:text-9xl bg-gradient-to-r from-[#c8102e] via-red-500 to-[#c8102e] bg-clip-text text-transparent">CREST.</span>
              <span className="block text-5xl sm:text-6xl md:text-8xl lg:text-9xl italic text-white/90">OWN IT.</span>
            </h1>
            <p className="mt-8 text-white/70 max-w-lg text-lg leading-relaxed">
              Every hoodie, cap, and tee is Patriots family. <span className="text-white font-semibold">Scan the QR</span>, tap <span className="text-[#c8102e] font-semibold">Pay via Autobook</span>, and gear up before Friday night lights.
            </p>

            {/* Trust badges */}
            <div className="mt-10 flex flex-wrap gap-6 text-white/80" data-testid="apparel-hero-trust">
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#c8102e]" /><span className="font-heading uppercase tracking-widest text-[10px]">Practice Pickup</span></div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#c8102e]" /><span className="font-heading uppercase tracking-widest text-[10px]">Autobook Secure</span></div>
              <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#c8102e]" /><span className="font-heading uppercase tracking-widest text-[10px]">Invoice Every Sale</span></div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#store-grid"
                data-testid="apparel-hero-shop-cta"
                className="w-full sm:w-auto justify-center group inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase text-xs px-6 sm:px-8 py-4 shadow-lg shadow-[#c8102e]/40 hover:shadow-[#c8102e]/70 transition-[background-color,box-shadow]"
              >
                <ShoppingBag className="w-4 h-4" /> Shop the Drop
              </a>
              <a
                href={autobookPayUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="apparel-hero-pay-cta"
                className="w-full sm:w-auto justify-center group inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-heading uppercase text-xs px-6 sm:px-8 py-4 transition-[background-color,border-color]"
              >
                <QrCode className="w-4 h-4" /> Pay via Autobook <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            </div>

            {/* Promo banners */}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <div
                className="inline-flex items-center gap-3 border border-dashed border-[#c8102e]/60 bg-[#c8102e]/10 backdrop-blur-sm px-5 py-3"
                data-testid="apparel-hero-promo-banner"
              >
                <Tag className="w-5 h-5 text-[#c8102e] shrink-0" />
                <div className="text-white text-sm leading-tight">
                  <span className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Hoodie Bundle</span>
                  <div className="mt-0.5">
                    Buy <span className="font-semibold text-white">2 hoodies</span>, save <span className="font-semibold text-[#c8102e]">$10</span> · code{' '}
                    <span
                      className="font-mono font-bold tracking-widest bg-white text-[#0a1f44] px-2 py-0.5 select-all"
                      data-testid="apparel-hero-promo-code"
                    >HOODIE10</span>
                  </div>
                </div>
              </div>
              <div
                className="inline-flex items-center gap-3 border border-dashed border-[#c8102e]/60 bg-[#c8102e]/10 backdrop-blur-sm px-5 py-3"
                data-testid="apparel-hero-promo-banner-tee"
              >
                <Tag className="w-5 h-5 text-[#c8102e] shrink-0" />
                <div className="text-white text-sm leading-tight">
                  <span className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Tee Bundle</span>
                  <div className="mt-0.5">
                    Buy <span className="font-semibold text-white">2 tees</span>, save <span className="font-semibold text-[#c8102e]">$5</span> · code{' '}
                    <span
                      className="font-mono font-bold tracking-widest bg-white text-[#0a1f44] px-2 py-0.5 select-all"
                      data-testid="apparel-hero-promo-code-tee"
                    >TEEBUNDLE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Featured product + QR */}
          <div className="flex flex-col items-center gap-6">
            {/* Featured card */}
            <div className="relative w-full max-w-sm group" data-testid="apparel-hero-featured">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#c8102e] via-red-500 to-[#c8102e] blur opacity-50 group-hover:opacity-90 transition-opacity" />
              <div className="relative bg-white overflow-hidden">
                <div className="bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5 flex items-center gap-2">
                  <Star className="w-3 h-3 fill-current" /> Featured Drop
                </div>
                <div className="relative aspect-square bg-[#0a1f44]/5 overflow-hidden">
                  <img src={featured.img} alt={featured.name} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-1000" decoding="async" />
                  <img src={iceLogo} alt="ICE" className="absolute top-3 right-3 h-10 w-auto opacity-90 drop-shadow" />
                </div>
                <div className="p-4">
                  <div className="font-display text-[#0a1f44] text-xl leading-tight">{featured.name.toUpperCase()}</div>
                  <div className="text-[#0a1f44]/60 text-xs font-heading tracking-wider mt-1">{featured.tagline}</div>
                  <button
                    onClick={() => openBuy(featured)}
                    data-testid="apparel-hero-featured-order-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-3 transition-colors"
                  >
                    <ShoppingBag className="w-3 h-3" /> Order This
                  </button>
                </div>
              </div>
            </div>

            {/* QR */}
            <div className="hidden sm:block bg-white p-3 shadow-2xl relative" data-testid="apparel-autobook-qr">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[9px] px-3 py-1">Scan → Pay</div>
              <img src={qrSrc} alt="Scan to pay via Autobook" className="w-40 h-40" />
              <a
                href={autobookPayUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="apparel-autobook-link"
                className="mt-2 flex items-center justify-center gap-1 text-[#c8102e] hover:text-[#e11d34] font-heading uppercase tracking-widest text-[9px] transition-colors"
              >
                app.autobook.co <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIPE */}
      <div className="bg-[#c8102e] text-white overflow-hidden py-3 border-y border-white/10" data-testid="apparel-marquee">
        <div className="flex whitespace-nowrap animate-[marquee_25s_linear_infinite] gap-12 font-heading uppercase tracking-[0.4em] text-sm">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-12">
              {item}
              <Star className="w-3 h-3 fill-current" />
            </span>
          ))}
        </div>
      </div>

      {/* Category filter */}
      <section className="bg-white border-b border-[#0a1f44]/10 sticky top-16 z-30" id="store-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-nowrap overflow-x-auto overscroll-contain gap-2 pb-1" data-testid="apparel-category-filter">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`apparel-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`shrink-0 min-h-11 px-4 sm:px-5 py-2 font-heading uppercase text-[11px] border-2 transition-[background-color,border-color,color,box-shadow] ${
                  category === c
                    ? 'bg-[#c8102e] text-white border-[#c8102e] shadow-md shadow-[#c8102e]/40'
                    : 'bg-white text-[#0a1f44] border-[#0a1f44]/15 hover:border-[#c8102e] hover:text-[#c8102e]'
                }`}
              >
                {c}
                <span className="ml-2 opacity-60 text-[10px]">
                  {c === 'All' ? apparelProducts.length : apparelProducts.filter((p) => p.category === c).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-16 bg-gradient-to-b from-[#f5f6f8] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 min-[390px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="apparel-product-grid">
            {filtered.map((p, index) => {
              const isHot = HOT_IDS.has(p.id);
              const isNew = NEW_IDS.has(p.id);
              const isLimited = LIMITED_IDS.has(p.id);
              return (
                <div key={p.id} data-testid={`product-card-${p.id}`} className="group relative bg-white border border-[#0a1f44]/10 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#0a1f44]/20 hover:border-[#c8102e]">
                  <div className="relative aspect-square bg-[#0a1f44]/5 overflow-hidden">
                    <img src={p.img} alt={p.name} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700" loading={index < 4 ? 'eager' : 'lazy'} fetchPriority={index < 2 ? 'high' : 'auto'} decoding="async" />
                    <img src={iceLogo} alt="ICE" className="absolute top-3 right-3 h-10 w-auto opacity-90 drop-shadow" />

                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col items-start gap-1">
                      <div className="bg-[#0a1f44] text-white font-heading uppercase tracking-widest text-[9px] px-2 py-1">{p.category}</div>
                      {isHot && (
                        <div className="bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[9px] px-2 py-1 flex items-center gap-1">
                          <Flame className="w-2.5 h-2.5" /> Hot
                        </div>
                      )}
                      {isNew && (
                        <div className="bg-emerald-600 text-white font-heading uppercase tracking-widest text-[9px] px-2 py-1 flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" /> New
                        </div>
                      )}
                      {isLimited && (
                        <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-[#0a1f44] font-heading uppercase tracking-widest text-[9px] px-2 py-1 flex items-center gap-1 shadow-md">
                          <Star className="w-2.5 h-2.5 fill-current" /> Limited
                        </div>
                      )}
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44]/95 via-[#0a1f44]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-5">
                      <button
                        onClick={() => openBuy(p)}
                        data-testid={`product-quick-order-btn-${p.id}`}
                        className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500 bg-[#c8102e] hover:bg-white hover:text-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-5 py-3 flex items-center gap-2 shadow-lg shadow-black/40"
                      >
                        <ShoppingBag className="w-3 h-3" /> Quick Order
                      </button>
                    </div>
                  </div>
                  <div className="p-3 sm:p-5">
                    <div className="font-display text-[#0a1f44] text-lg sm:text-xl leading-tight group-hover:text-[#c8102e] transition-colors">{p.name.toUpperCase()}</div>
                    <div className="text-[#0a1f44]/60 text-xs font-heading tracking-wider mt-1 line-clamp-1">{p.tagline}</div>
                    {soldOutSummary(p.id) && <div className="mt-2 font-heading uppercase text-[9px] text-[#c8102e]" data-testid={`size-summary-${p.id}`}>{soldOutSummary(p.id)}</div>}
                    <div className="mt-4 flex items-center justify-between">
                      <div data-testid={`product-price-${p.id}`}>
                        {p.price ? (
                          <span className="font-display text-2xl text-[#c8102e]">${p.price}</span>
                        ) : (
                          <span className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 border border-[#0a1f44]/20 px-2 py-1">Price TBA</span>
                        )}
                      </div>
                      <button
                        onClick={() => openBuy(p)}
                        data-testid={`product-order-btn-${p.id}`}
                        className="min-h-11 inline-flex items-center gap-2 bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase text-[10px] px-3 sm:px-4 py-2 transition-colors"
                      >
                        <ShoppingBag className="w-3 h-3" /> Order
                      </button>
                    </div>

                    {/* Limited edition progress bar (ICE only) */}
                    {p.id === 'hoodie-red' && iceStock && (
                      <div className="mt-4" data-testid="ice-progress">
                        <div className="flex items-center justify-between text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">
                          <span className="text-[#c8102e]">Limited Run</span>
                          <span data-testid="ice-progress-count">
                            {iceStock.remaining > 0
                              ? `${iceStock.remaining} of ${iceStock.limit} left`
                              : 'Sold Out'}
                          </span>
                        </div>
                        <div className="h-2 bg-[#0a1f44]/10 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${iceStock.remaining === 0 ? 'bg-[#0a1f44]/40' : 'bg-[#c8102e]'}`}
                            style={{ width: `${Math.max(0, Math.min(100, (iceStock.remaining / iceStock.limit) * 100))}%` }}
                            data-testid="ice-progress-bar"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 sm:mt-16 relative bg-[#0a1f44] p-6 sm:p-10 md:p-14 overflow-hidden" data-testid="apparel-bottom-cta">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#c8102e]/40 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="relative grid md:grid-cols-[1.5fr_1fr] gap-8 items-center">
              <div>
                <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-3">Ready to Rep?</div>
                <h2 className="font-display text-white text-4xl md:text-5xl leading-tight">DON'T WAIT.<br />GEAR UP FOR GAME DAY.</h2>
                <p className="mt-4 text-white/70 max-w-lg">Scan the QR to pay directly via Autobook. Every order is invoiced and tracked in our admin console for total accountability.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#store-grid"
                    className="bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-6 py-3 transition-colors shadow-lg shadow-[#c8102e]/40"
                  >
                    Browse Store
                  </a>
                  <a
                    href={autobookPayUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-heading uppercase tracking-widest text-[10px] px-6 py-3 transition-colors"
                  >
                    Pay via Autobook →
                  </a>
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="bg-white p-3 shadow-2xl">
                  <img src={qrSrc} alt="Pay via Autobook" className="w-40 h-40" />
                  <div className="mt-2 text-center font-heading uppercase tracking-widest text-[9px] text-[#0a1f44]">Scan · Pay · Wear</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PaymentSection variant="apparel" />

      {/* Buy modal */}
      {activeProduct && (
        <div
          className="fixed inset-0 z-[60] bg-[#0a1f44]/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeModal}
          data-testid="order-modal"
        >
          <div
            className="bg-white w-full max-w-lg border-t-4 border-[#c8102e] max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto overscroll-contain shadow-2xl pb-[env(safe-area-inset-bottom)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 sm:p-6 border-b border-[#0a1f44]/10 sticky top-0 bg-white z-10">
              <div>
                <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">
                  {confirmation ? 'Order Received' : 'Place Order'}
                </div>
                <div className="font-display text-[#0a1f44] text-2xl leading-tight mt-1">{activeProduct.name.toUpperCase()}</div>
              </div>
              <button
                onClick={closeModal}
                data-testid="order-modal-close"
                className="p-2 text-[#0a1f44]/60 hover:text-[#c8102e] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {confirmation ? (
              <div className="p-4 sm:p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center mb-4">
                  <Check className="w-8 h-8" />
                </div>
                <div className="font-display text-[#0a1f44] text-2xl mb-2">THANK YOU!</div>
                <p className="text-[#0a1f44]/70 text-sm mb-4">Your order for <span className="font-semibold">{confirmation.productName}</span> is saved.</p>
                <div className="bg-[#0a1f44]/5 p-4 mb-6 text-left" data-testid="order-confirmation">
                  <div className="text-[#0a1f44]/50 font-heading uppercase tracking-widest text-[9px] mb-1">Invoice #</div>
                  <div className="font-display text-[#0a1f44] text-xl" data-testid="order-invoice-number">{confirmation.invoiceNumber}</div>
                  {confirmation.total > 0 && (
                    <>
                      {confirmation.discount > 0 && (
                        <div className="mt-3 space-y-1 text-sm" data-testid="order-confirm-discount-block">
                          <div className="flex justify-between text-[#0a1f44]/70">
                            <span>Subtotal</span>
                            <span className="font-mono">${(confirmation.subtotal || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-emerald-700">
                            <span>Promo · {confirmation.promoCode}</span>
                            <span className="font-mono">−${(confirmation.discount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      <div className="text-[#0a1f44]/50 font-heading uppercase tracking-widest text-[9px] mt-3 mb-1">Amount Due</div>
                      <div className="font-display text-[#c8102e] text-2xl">${confirmation.total.toFixed(2)}</div>
                      <button type="button" data-testid="order-pay-stripe" onClick={async () => { try { const { checkout_url } = await startCheckout({ kind: 'apparel_order', order_id: confirmation.orderId }); window.location.assign(checkout_url); } catch (err) { toast({ title: 'Could not start checkout', description: err.response?.data?.detail || err.message }); } }} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-xs px-6 py-4 transition-colors"><ShieldCheck className="w-4 h-4" /> Pay ${confirmation.total.toFixed(2)} Securely with Stripe</button>
                      <div className="text-[10px] text-[#0a1f44]/50 mt-2">Card payments processed by Stripe · receipt emailed automatically · or pay at pickup using the options below</div>
                    </>
                  )}
                </div>

                {/* Pickup confirmation */}
                <div className="bg-[#0a1f44] text-white p-5 mb-6 text-left" data-testid="order-pickup-confirmation">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="w-4 h-4 text-[#c8102e]" />
                    <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Local Pickup</div>
                  </div>
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-white/70 shrink-0" />
                    <a href={PICKUP_MAP_URL} target="_blank" rel="noreferrer" className="text-white underline decoration-[#c8102e]/50 hover:decoration-[#c8102e] text-sm" data-testid="order-confirm-location">
                      {confirmation.pickupLocation}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-white/70 shrink-0" />
                    <div className="text-sm">
                      Ready by <span className="font-semibold text-[#c8102e]" data-testid="order-confirm-ready">{confirmation.pickupReadyDate || pickupReady}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[#0a1f44]/70 text-sm mb-4 flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4 text-[#c8102e]" /> A copy will be forwarded to <span className="font-semibold">{invoiceEmail}</span> for accountability.
                </p>
                <div className="bg-white border-2 border-[#c8102e] p-3 inline-block mb-4">
                  <img src={qrSrc} alt="Scan to pay" className="w-40 h-40" />
                  <div className="mt-2 font-heading uppercase tracking-widest text-[9px] text-[#0a1f44]">Scan to Pay</div>
                </div>
                <a
                  href={autobookPayUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-4 transition-colors"
                  data-testid="order-pay-now-btn"
                >
                  Pay Now via Autobook <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-4 sm:p-6 space-y-4">
                {/* Local Pickup callout */}
                <div className="bg-[#0a1f44]/[0.04] border-l-4 border-[#c8102e] p-4" data-testid="order-pickup-info">
                  <div className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-[#c8102e] mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mb-1">Fulfillment · Local Pickup</div>
                      <div className="flex items-start gap-1 text-[#0a1f44] mb-1">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <a href={PICKUP_MAP_URL} target="_blank" rel="noreferrer" className="underline decoration-[#c8102e]/40 hover:decoration-[#c8102e] font-semibold" data-testid="order-pickup-location">
                          {PICKUP_LOCATION}
                        </a>
                      </div>
                      <div className="flex items-center gap-1 text-[#0a1f44]/80">
                        <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                        <span data-testid="order-pickup-ready">Ready for pickup: <span className="font-semibold text-[#c8102e]">{pickupReady}</span> <span className="text-[#0a1f44]/50">({PICKUP_LEAD_DAYS} days after order)</span></span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
                  {activeProduct.variants.length > 0 && (
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Color</label>
                      <select
                        value={form.variant}
                        onChange={(e) => setForm({ ...form, variant: e.target.value })}
                        data-testid="order-form-variant"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                      >
                        {activeProduct.variants.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Size</label>
                    <select
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      data-testid="order-form-size"
                      className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                    >
                      {activeProduct.sizes.map((s) => {
                        const available = isSizeAvailable(activeProduct.id, s);
                        return <option key={s} value={s} disabled={!available} data-testid={`size-option-${activeProduct.id}-${s.toLowerCase()}`}>{sizeLabel(s)}{available ? '' : ' — Sold Out'}</option>;
                      })}
                    </select>
                    {soldOutSummary(activeProduct.id) && <div className="mt-2 text-[10px] font-heading uppercase text-[#0a1f44]/60" data-testid="size-status">{soldOutSummary(activeProduct.id)}</div>}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Quantity</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    data-testid="order-form-quantity"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Your Name *</label>
                  <input
                    required
                    type="text"
                    value={form.customerName}
                    onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                    data-testid="order-form-name"
                    autoComplete="name"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Email *</label>
                  <input
                    required
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                    data-testid="order-form-email"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={form.customerPhone}
                    onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                    data-testid="order-form-phone"
                    autoComplete="tel"
                    inputMode="tel"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Notes (optional)</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="order-form-notes"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]"
                    placeholder="e.g. Player name, pickup preference…"
                  />
                </div>

                {/* Promo code */}
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1 flex items-center gap-1">
                    <Tag className="w-3 h-3 text-[#c8102e]" /> Promo Code (optional)
                  </label>
                  <input
                    type="text"
                    value={form.promoCode}
                    onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase() })}
                    data-testid="order-form-promo"
                    placeholder="HOODIE10 (2+ hoodies) or TEEBUNDLE (2+ tees)"
                    className={`w-full border px-3 py-2 focus:outline-none uppercase tracking-widest font-mono ${
                      promoResult?.valid
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : form.promoCode
                          ? 'border-[#c8102e]/40 focus:border-[#c8102e]'
                          : 'border-[#0a1f44]/20 focus:border-[#c8102e]'
                    }`}
                  />
                  {form.promoCode && promoResult && (
                    <div
                      className={`mt-1 text-[11px] ${promoResult.valid ? 'text-emerald-700' : 'text-[#0a1f44]/60'}`}
                      data-testid="order-form-promo-message"
                    >
                      {promoResult.valid ? '✓ ' : ''}{promoResult.message}
                    </div>
                  )}
                </div>

                {/* Live totals */}
                <div className="bg-[#0a1f44]/[0.04] p-3 space-y-1 text-sm" data-testid="order-form-totals">
                  {promoResult?.valid ? (
                    <>
                      <div className="flex justify-between text-[#0a1f44]/70">
                        <span>Subtotal</span>
                        <span className="font-mono">${(promoResult.subtotal || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-700">
                        <span>Promo · {promoResult.code}</span>
                        <span className="font-mono" data-testid="order-form-discount">−${(promoResult.discount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[#0a1f44] font-semibold border-t border-[#0a1f44]/10 pt-1 mt-1">
                        <span>Total</span>
                        <span className="font-mono text-[#c8102e]" data-testid="order-form-total">${(promoResult.total || 0).toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-[#0a1f44] font-semibold">
                      <span>Total</span>
                      <span className="font-mono text-[#c8102e]" data-testid="order-form-total">
                        ${(((parseFloat(form.unitPrice) || 0) * (parseInt(form.quantity, 10) || 1))).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || !isSizeAvailable(form.productId, form.size)}
                  data-testid="order-form-submit"
                  className="w-full bg-[#c8102e] hover:bg-[#e11d34] disabled:bg-[#0a1f44]/40 text-white font-heading uppercase tracking-widest text-xs px-6 py-4 transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  {submitting ? 'Placing Order…' : 'Submit Order'}
                </button>
                <p className="text-[10px] text-[#0a1f44]/50 font-heading uppercase tracking-widest text-center">
                  Prices coming soon — you'll pay via Autobook after receiving the invoice.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
